import { fmt } from '../lib/api';
import { Modal } from '../screens/Till';

const STORE_NAMES: Record<string, { en: string; ar: string }> = {
  BH21101: { en: 'City Centre Bahrain', ar: 'سيتي سنتر البحرين' },
  BH21103: { en: 'The Avenues', ar: 'الأفنيوز' },
  BH21102: { en: 'Warehouse Bahrain', ar: 'مستودع البحرين' },
};

/**
 * Simplified TAX INVOICE, bilingual EN/AR.
 *
 * Bahrain NBR requires a tax invoice to show the supplier's VAT registration
 * number (TRN), a sequential invoice number, the VAT amount, and the taxable
 * value. The TRN is read from VITE_VAT_TRN — it MUST be set before going live,
 * and the receipt says so loudly rather than printing a blank.
 */
export default function ReceiptModal({ receipt, onClose }: { receipt: any; onClose: () => void }) {
  const trn = (import.meta as any).env?.VITE_VAT_TRN as string | undefined;
  const store = STORE_NAMES[receipt.storeCode] ?? { en: receipt.storeCode, ar: '' };
  const t = receipt.totals;

  return (
    <Modal title={receipt.synced ? 'Receipt' : 'Receipt (queued)'} onClose={onClose} width="max-w-sm">
      <div className="bg-white text-black rounded-lg p-5 font-mono text-[11px] leading-relaxed print:shadow-none"
           id="receipt-print">
        <div className="text-center">
          <div className="text-lg font-bold tracking-widest">FEMI9</div>
          <div className="text-[10px]">{store.en}</div>
          {store.ar && <div className="text-[10px]" dir="rtl">{store.ar}</div>}
          <div className="mt-1 font-bold text-[10px]">TAX INVOICE / فاتورة ضريبية</div>
          {trn
            ? <div className="text-[10px]">VAT TRN: {trn}</div>
            : <div className="text-[10px] text-red-600 font-bold">VAT TRN NOT CONFIGURED</div>}
        </div>

        <div className="border-t border-dashed border-black my-2" />

        <div className="flex justify-between"><span>Receipt</span><span className="font-bold">{receipt.receiptNo}</span></div>
        <div className="flex justify-between"><span>Date</span><span>{new Date(receipt.at).toLocaleString('en-GB')}</span></div>
        <div className="flex justify-between"><span>Till</span><span>{receipt.storeCode}/{receipt.terminalCode}</span></div>
        <div className="flex justify-between"><span>Served by</span><span>{receipt.cashier}</span></div>

        <div className="border-t border-dashed border-black my-2" />

        {receipt.lines.map((l: any) => (
          <div key={l.key} className="mb-1.5">
            <div className="truncate">{l.title}</div>
            <div className="flex justify-between text-[10px]">
              <span>{l.sku}{l.size ? ` ${l.size}` : ''}</span>
            </div>
            <div className="flex justify-between">
              <span>{l.quantity} x {fmt(l.unitGrossMinor)}</span>
              <span className="font-bold">{fmt(l.quantity * l.unitGrossMinor - l.discountMinor)}</span>
            </div>
            {l.discountMinor > 0 && (
              <div className="flex justify-between text-[10px]"><span>  discount</span><span>−{fmt(l.discountMinor)}</span></div>
            )}
          </div>
        ))}

        <div className="border-t border-dashed border-black my-2" />

        <div className="flex justify-between"><span>Taxable value / القيمة</span><span>{fmt(t.net)}</span></div>
        <div className="flex justify-between"><span>VAT 10% / ضريبة</span><span>{fmt(t.vat)}</span></div>
        {t.discount > 0 && <div className="flex justify-between"><span>Discount</span><span>−{fmt(t.discount)}</span></div>}
        {t.rounding !== 0 && (
          <div className="flex justify-between"><span>Rounding</span><span>{t.rounding > 0 ? '+' : ''}{t.rounding}</span></div>
        )}
        <div className="flex justify-between text-sm font-bold border-t border-black mt-1 pt-1">
          <span>TOTAL BHD</span><span>{fmt(t.payableCash)}</span>
        </div>

        <div className="border-t border-dashed border-black my-2" />

        {receipt.payments.map((p: any, i: number) => (
          <div key={i} className="flex justify-between">
            <span className="capitalize">{p.method.replace('_', ' ')}{p.cardScheme ? ` ${p.cardScheme}` : ''}</span>
            <span>{fmt(p.amountMinor)}</span>
          </div>
        ))}
        {receipt.payments.some((p: any) => p.tenderedMinor) && (
          <>
            <div className="flex justify-between">
              <span>Tendered</span>
              <span>{fmt(receipt.payments.reduce((s: number, p: any) => s + (p.tenderedMinor ?? 0), 0))}</span>
            </div>
            <div className="flex justify-between font-bold">
              <span>Change</span>
              <span>{fmt(Math.max(0, receipt.payments.reduce((s: number, p: any) => s + (p.tenderedMinor ?? 0), 0) - t.payableCash))}</span>
            </div>
          </>
        )}

        <div className="border-t border-dashed border-black my-2" />

        <div className="text-center text-[10px]">
          <div>Exchange within 14 days with this receipt</div>
          <div dir="rtl">الاستبدال خلال ١٤ يوماً مع هذه الفاتورة</div>
          <div className="mt-1.5 font-bold">Thank you · شكراً لزيارتكم</div>
          <div className="mt-1 tracking-[0.3em]">{receipt.receiptNo}</div>
        </div>
      </div>

      {!receipt.synced && (
        <div className="mt-3 text-xs text-sky-300 bg-sky-950/40 border border-sky-900 rounded px-3 py-2">
          This sale is queued and will sync automatically. The receipt number is
          assigned by the server on sync — the sale itself is already recorded and safe.
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button className="btn-ghost flex-1" onClick={() => window.print()}>Print</button>
        <button className="btn-primary flex-1" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}
